import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink, Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import {
  ListingCardComponent,
  ListingSkeletonComponent,
} from '@out-web/components';
import { Meta, Title } from '@angular/platform-browser';

import {
  IListing,
  ListingsService,
  LocalAuthService,
  Auth0User,
} from '@out-web/core';
import { Observable } from 'rxjs';

interface Tab {
  id: string;
  label: string;
  count?: number; // Add count property
}

@Component({
  selector: 'out-account.component',
  imports: [
    CommonModule,
    RouterLink,
    ListingCardComponent,
    ListingSkeletonComponent,
  ],
  templateUrl: './account.component.html',
  styleUrl: './account.component.scss',
})
export class AccountComponent implements OnInit {
  activeTab = 'live';
  isLoading = true;
  listings: IListing[] = [];

  user$: Observable<Auth0User>;

  tabs: Tab[] = [
    { id: 'live', label: 'My Live Listings' },
    { id: 'draft', label: 'My Draft Listings' },
  ];

  constructor(
    public auth: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private listingsService: ListingsService,
    private localAuthService: LocalAuthService,
    private meta: Meta,
    private titleService: Title
  ) {
    this.user$ = this.localAuthService.user$;
  }

  ngOnInit(): void {
    this.setMetaData();
    this.fetchListingData();
    // Read the activeTab from the URL (if any)
    this.route.queryParamMap.subscribe(params => {
      const activeTabParam = params.get('act');
      if (activeTabParam) {
        this.activeTab = activeTabParam;
      }
    });
  }

  setActiveTab(tabId: string): void {
    this.activeTab = tabId;
    // Update the URL with the activeTab parameter while merging other query parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { act: tabId },
      queryParamsHandling: 'merge'
    });
  }

  isTabActive(tabId: string): boolean {
    return this.activeTab === tabId;
  }

  get liveListings() {
    return this.listings.filter((listing) => listing.status === 'Published');
  }

  get draftListings() {
    return this.listings.filter((listing) => listing.status === 'New');
  }

  updateTabCounts(): void {
    this.tabs = [
      {
        id: 'live',
        label: 'My Live Listings',
        count: this.liveListings.length,
      },
      {
        id: 'draft',
        label: 'My Draft Listings',
        count: this.draftListings.length,
      },
    ];
  }

  private fetchListingData() {
    this.listingsService.fetchMyListings().subscribe({
      next: (response) => {
        this.listings = response;
        this.updateTabCounts(); // Update counts when listings are loaded
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching listings:', error);
        this.isLoading = false; // Stop loading on error
      },
    });
  }

  setMetaData() {
    this.titleService.setTitle('Account | Sell Your Tesla | Only Used Tesla');
    this.meta.addTags([
      {
        name: 'Account | Sell Your Tesla | Only Used Tesla',
        property: 'og:title',
        content: 'Account | Sell Your Tesla | Only Used Tesla',
      },
    ]);
  }
}
